import {
  GameWinnerPrediction,
  LineupPlayer,
  NormalizedPrediction,
  NormalizedScheduledGame,
  NormalizedWithheldGame,
  ScheduledGame,
  StartingPitcher,
  WithheldGame,
} from "./types";
import { validateStrictPredictionArtifact } from "./validation";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? "";
const PREDICTIONS_PATH = process.env.REACT_APP_PREDICTIONS_PATH ?? "/predictions/latest.json";

function toNumber(value: unknown): number | null {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return null;
  }
  return value;
}

function normalizeLineup(lineup: unknown): LineupPlayer[] {
  if (!Array.isArray(lineup)) {
    return [];
  }

  return lineup
    .filter((item): item is LineupPlayer => typeof item === "object" && item !== null)
    .map((item) => ({
      player_id: Number(item.player_id),
      batting_order_slot: Number(item.batting_order_slot),
      player_name: item.player_name ?? null,
      season_to_date_batting: item.season_to_date_batting ?? null,
    }))
    .filter((item) => !Number.isNaN(item.player_id) && !Number.isNaN(item.batting_order_slot))
    .sort((a, b) => a.batting_order_slot - b.batting_order_slot);
}

function normalizeStartingPitcher(pitcher: unknown): StartingPitcher | null {
  if (typeof pitcher !== "object" || pitcher === null || Array.isArray(pitcher)) {
    return null;
  }

  const pitcherId = Number((pitcher as Record<string, unknown>).player_id);
  if (Number.isNaN(pitcherId)) {
    return null;
  }

  const raw = pitcher as Record<string, unknown>;
  return {
    player_id: pitcherId,
    player_name: typeof raw.player_name === "string" ? raw.player_name : null,
    throws_hand: typeof raw.throws_hand === "string" ? raw.throws_hand : null,
    season_to_date_pitching:
      typeof raw.season_to_date_pitching === "object" && raw.season_to_date_pitching !== null
        ? {
            era: toNumber((raw.season_to_date_pitching as Record<string, unknown>).era),
            whip: toNumber((raw.season_to_date_pitching as Record<string, unknown>).whip),
            innings_pitched: toNumber((raw.season_to_date_pitching as Record<string, unknown>).innings_pitched),
            strike_outs: toNumber((raw.season_to_date_pitching as Record<string, unknown>).strike_outs),
            base_on_balls: toNumber((raw.season_to_date_pitching as Record<string, unknown>).base_on_balls),
            wins: toNumber((raw.season_to_date_pitching as Record<string, unknown>).wins),
            losses: toNumber((raw.season_to_date_pitching as Record<string, unknown>).losses),
            games_started: toNumber((raw.season_to_date_pitching as Record<string, unknown>).games_started),
            quality_starts: toNumber((raw.season_to_date_pitching as Record<string, unknown>).quality_starts),
          }
        : null,
  };
}

function normalizePrediction(row: GameWinnerPrediction): NormalizedPrediction {
  const venueTimeZone =
    typeof row.venue_time_zone === "string" && row.venue_time_zone.trim()
      ? row.venue_time_zone.trim()
      : null;

  return {
    gameId: row.game_id,
    gameDate: row.game_date,
    gameStartTimeUtc: row.game_start_time_utc ?? null,
    venueTimeZone,
    gameStatus: row.game_status ?? null,
    statusDetailed: row.status_detailed ?? null,
    homeTeam: row.home_team,
    awayTeam: row.away_team,
    homeTeamCity: row.home_team_city ?? null,
    awayTeamCity: row.away_team_city ?? null,
    homeTeamLocation: row.home_team_location ?? row.home_team_city ?? null,
    awayTeamLocation: row.away_team_location ?? row.away_team_city ?? null,
    predictedWinner: row.winner ?? row.predicted_winner ?? null,
    finalConfidence: toNumber(row.final_confidence),
    lineupConfirmed:
      typeof row.lineups_confirmed_at_run === "boolean"
        ? row.lineups_confirmed_at_run
        : typeof row.lineups_confirmed === "boolean"
          ? row.lineups_confirmed
          : null,
    actualHomeScore: row.actual_home_score != null ? Number(row.actual_home_score) : null,
    actualAwayScore: row.actual_away_score != null ? Number(row.actual_away_score) : null,
    actualWinner: typeof row.actual_winner === "string" ? row.actual_winner : null,
    predictionCorrect: typeof row.prediction_correct === "boolean" ? row.prediction_correct : null,
    homeStartingLineup: normalizeLineup(row.home_starting_lineup),
    awayStartingLineup: normalizeLineup(row.away_starting_lineup),
    homeStartingPitcher: normalizeStartingPitcher(row.home_starting_pitcher),
    awayStartingPitcher: normalizeStartingPitcher(row.away_starting_pitcher),
  };
}

function normalizeWithheldGame(row: WithheldGame): NormalizedWithheldGame {
  const homeTeam = typeof row.home_team === "string" && row.home_team.trim() ? row.home_team : null;
  const awayTeam = typeof row.away_team === "string" && row.away_team.trim() ? row.away_team : null;
  const venueTimeZone =
    typeof row.venue_time_zone === "string" && row.venue_time_zone.trim()
      ? row.venue_time_zone.trim()
      : null;

  return {
    gameId: row.game_id,
    gameDate: row.game_date,
    gameStartTimeUtc: row.game_start_time_utc,
    gameType: row.game_type,
    venueTimeZone,
    homeTeam,
    awayTeam,
    lineupsConfirmedAtRun: row.lineups_confirmed_at_run,
    homeStartingPitcherIdPresent: row.home_starting_pitcher_id_present,
    awayStartingPitcherIdPresent: row.away_starting_pitcher_id_present,
    namedStartersPresent: row.named_starters_present,
    homeLineupSlotCount: row.home_lineup_slot_count,
    awayLineupSlotCount: row.away_lineup_slot_count,
    readinessReasonCodes: row.readiness_reason_codes,
    statusMessageCode: row.status_message_code,
    minutesToFirstPitchAtRun: row.minutes_to_first_pitch_at_run,
    cadenceBucketAtRun: row.cadence_bucket_at_run,
    selectionReason: row.selection_reason,
    statusAbstract: row.status_abstract,
    statusDetailed: row.status_detailed,
  };
}

export function sortWithheldByStartTime(games: NormalizedWithheldGame[]): NormalizedWithheldGame[] {
  return [...games].sort((a, b) => {
    const aMs = new Date(a.gameStartTimeUtc).getTime();
    const bMs = new Date(b.gameStartTimeUtc).getTime();

    if (Number.isNaN(aMs) && Number.isNaN(bMs)) return 0;
    if (Number.isNaN(aMs)) return 1;
    if (Number.isNaN(bMs)) return -1;
    return aMs - bMs;
  });
}

function normalizeScheduledGame(row: ScheduledGame): NormalizedScheduledGame {
  const homeTeam = typeof row.home_team === "string" && row.home_team.trim() ? row.home_team : null;
  const awayTeam = typeof row.away_team === "string" && row.away_team.trim() ? row.away_team : null;
  const venueTimeZone =
    typeof row.venue_time_zone === "string" && row.venue_time_zone.trim()
      ? row.venue_time_zone.trim()
      : null;

  return {
    gameId: row.game_id,
    gameDate: row.game_date,
    gameStartTimeUtc: row.game_start_time_utc,
    gameType: row.game_type,
    venueTimeZone,
    homeTeam,
    awayTeam,
    statusAbstract: row.status_abstract,
    statusDetailed: row.status_detailed,
    statusReason: row.status_reason,
    lineupsConfirmed: row.lineups_confirmed,
    namedStartersPresent: row.named_starters_present,
    homeLineupSlotCount: row.home_lineup_slot_count,
    awayLineupSlotCount: row.away_lineup_slot_count,
  };
}

function sortScheduledByStartTime(games: NormalizedScheduledGame[]): NormalizedScheduledGame[] {
  return [...games].sort((a, b) => {
    const aMs = new Date(a.gameStartTimeUtc).getTime();
    const bMs = new Date(b.gameStartTimeUtc).getTime();

    if (Number.isNaN(aMs) && Number.isNaN(bMs)) return 0;
    if (Number.isNaN(aMs)) return 1;
    if (Number.isNaN(bMs)) return -1;
    return aMs - bMs;
  });
}

export function isStale(updatedAt?: string): boolean {
  if (!updatedAt) {
    return true;
  }
  const updated = new Date(updatedAt).getTime();
  if (Number.isNaN(updated)) {
    return true;
  }
  const ageMs = Date.now() - updated;
  return ageMs > 30 * 60 * 1000;
}

export async function fetchPredictions(signal?: AbortSignal): Promise<{
  predictions: NormalizedPrediction[];
  withheldGames: NormalizedWithheldGame[];
  scheduledGames: NormalizedScheduledGame[];
  stale: boolean;
  partial: boolean;
  lastUpdatedAt: string | null;
  runDate: string | null;
  withheldGamesCount: number;
  scheduledGamesCount: number;
}> {
  const response = await fetch(`${API_BASE_URL}${PREDICTIONS_PATH}`, { signal });

  if (!response.ok) {
    throw new Error(`Prediction API returned status ${response.status}`);
  }

  const rawPayload = await response.json();
  const payload = validateStrictPredictionArtifact(rawPayload);
  const rows = Array.isArray(payload.predictions) ? payload.predictions : [];
  const withheldRows = Array.isArray(payload.withheld_games) ? payload.withheld_games : [];
  const scheduledRows = Array.isArray(payload.scheduled_games) ? payload.scheduled_games : [];
  const normalized = rows.map(normalizePrediction);
  const normalizedWithheldGames = sortWithheldByStartTime(withheldRows.map(normalizeWithheldGame));
  const normalizedScheduledGames = sortScheduledByStartTime(scheduledRows.map(normalizeScheduledGame));
  const derivedPartial = normalized.some(
    (row) => !row.homeTeam || !row.awayTeam || !row.gameDate || row.predictedWinner === null || row.finalConfidence === null,
  );

  return {
    predictions: normalized,
    withheldGames: normalizedWithheldGames,
    scheduledGames: normalizedScheduledGames,
    stale: isStale(payload.last_updated_at),
    partial: payload.run_status === "partial_success" || payload.run_status === "failed" || derivedPartial,
    lastUpdatedAt: typeof payload.last_updated_at === "string" ? payload.last_updated_at : null,
    runDate: typeof payload.run_context?.run_date === "string" ? payload.run_context.run_date : null,
    withheldGamesCount: payload.summary?.withheld_games_count ?? normalizedWithheldGames.length,
    scheduledGamesCount: payload.summary?.scheduled_games_count ?? normalizedScheduledGames.length,
  };
}
